import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { TextField } from 'src/components/TextField';
import { fetchAllLifecycleStages } from 'src/models/lifecycle/lifecycle';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';

import { FilterTypeComponentProps } from './types';

export const FilterTypeLifecycle: FC<FilterTypeComponentProps> = ({
  value,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'reminderFilters.filterSet',
  });

  const { data: lifecycleStages = [] } = useQuery({
    queryKey: ['lifecycleStages'],
    queryFn: () => fetchAllLifecycleStages(),
    staleTime: 60000,
    gcTime: 60000,
  });

  const [editing, setEditing] = useState(false);

  const handleToggle = () => setEditing((e) => !e);

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {editing ? (
        <>
          {t('is')}
          <TextField
            value={value.negate ? 'not' : 'is'}
            onChange={(e) =>
              onChange({ ...value, negate: e.target.value === 'not' })
            }
            options={[
              { value: 'is', label: t('inStage') },
              { value: 'not', label: t('notInStage') },
            ]}
            sx={{ minWidth: 110 }}
          />
          {t('of')}
          <BaseSelect
            value={value.value ?? ''}
            onChange={(next) => onChange({ ...value, value: next || null })}
            options={lifecycleStages.map((s) => ({
              id: s.id.toString(),
              label: s.name,
            }))}
            placeholder={t('anyStage')}
            sx={{ minWidth: 200 }}
          />
          <Button color="primary" variant="prominent" onClick={handleToggle}>
            {t('done')}
          </Button>
        </>
      ) : (
        <>
          {t('is')} {value.negate && t('not')}{' '}
          {value.value ? (
            <>
              {t('inStage')}{' '}
              <i>
                {lifecycleStages.find((s) => s.id.toString() == value.value)
                  ?.name ?? t('noStageFound')}
              </i>
            </>
          ) : (
            t('inAnyStage')
          )}
          <IconButton size="small" color="primary" onClick={handleToggle}>
            <Icon icon={faEdit} size="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete()}>
            <Icon icon={faTrash} size="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};
